import React from 'react';
import {debounce} from 'lodash';
import {arrayMove} from '@dnd-kit/sortable';

import {emptyArray} from '@packages/utilities/fp/utils';
import {fixedForwardRef} from '../../utils';
import {useFloatingBase} from '../../hooks';
import {useFormGroupId} from '../FormGroup';
// Intentionally excluding SearchableListWrapper from SearchableList/index to avoid exposing it to consumers of Spring
import {SearchableListWrapper} from '../SearchableList/SearchableList';

import {AutocompleteInput} from './AutocompleteInput';
import {AutocompleteListBox} from './AutocompleteListBox';

import type {AutocompleteProps, AutocompleteOptions} from './_types';
import {AutocompleteOption} from './AutocompleteOption';

const AutocompleteBase = <TOptions extends AutocompleteOptions>(
  {
    'aria-errormessage': ariaErrorMessage,
    'aria-invalid': ariaInvalid,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    checkmarkStyle,
    className,
    'data-automation-id': dataAutomationId,
    defaultIsOpen = false,
    dropdownStyles,
    filterOptions,
    hasGroupDividers = true,
    id: idProp,
    isClearable: isClearableProp,
    isCreatable,
    isDisabled,
    isError,
    isLoading,
    isOpen: controlledIsOpen,
    isVirtualized = false,
    maxHeight,
    note,
    onBlur: onBlurProp,
    onChange,
    onCreateOption,
    onFocus: onFocusProp,
    onInputChange: onInputChangeProp,
    onKeyDown,
    onMenuClose,
    onMenuOpen,
    onOpenChange: onOpenChangeProp,
    filterDebounceDelay = 150,
    multiValueColor,
    multiValueVariant,
    isMultiValueReorderable = false,
    onReorder,
    onReorderComplete,
    renderCreateIcon,
    renderCreateLabel,
    renderEmptyStateLabel,
    renderOption: renderOptionProp,
    renderOptionLabel: renderOptionLabelProp,
    renderSelectedLabel: renderSelectedLabelProp,
    selectionMode = 'single',
    shouldCloseOnScroll = false,
    shouldCloseOnSelect = true,
    shouldFilterOverrideOptionsProp,
    shouldHideDropdownButton = false,
    shouldHideSelectedOptions = false,
    shouldHideTagDismissButton = false,
    shouldOpenOnFocus = false,
    shouldPreventBodyScroll = false,
    tagMaxWidth,
    placeholder,
    options = emptyArray as [],
    value = '',
    getSelectedOptionByValue,
    ...props
  }: AutocompleteProps<TOptions>,
  ref: React.Ref<HTMLInputElement>
) => {
  const isFocusedFromCloseRef = React.useRef(false);
  // Tracks the reorderable multi-select cursor position (the gap between chips)
  // so a newly selected item can be inserted at the cursor in a single write,
  // rather than appended and then reordered. Reported by AutocompleteInput.
  const reorderCaretRef = React.useRef<number | null>(null);
  const handleReorderCaretChange = React.useCallback((caret: number | null) => {
    reorderCaretRef.current = caret;
  }, []);
  const [uncontrolledInputValue, setUncontrolledInputValue] =
    React.useState('');
  const [uncontrolledIsOpen, setUncontrolledIsOpen] =
    React.useState(defaultIsOpen);

  /**
   * Tracks previous inputValue to determine if inputValue changed between renders.
   */
  const prevInputValueRef = React.useRef(uncontrolledInputValue);

  /**
   * Determines whether the input is clearable. If so, it renders a clear button.
   *
   * Default behavior:
   * - If `isClearableProp` is *not* provided:
   *   - The input is clearable if `selectionMode` is 'multiple' AND the value has been set.
   *  - The input is not clearable if `selectionMode` is 'single'.
   *
   * Override:
   * - If `isClearableProp` is provided, it takes precedence.
   */
  const hasValue = Array.isArray(value) ? value.length > 0 : value !== '';
  const isClearable =
    hasValue && (isClearableProp ?? selectionMode === 'multiple');
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;

  const setControlledOpen =
    controlledIsOpen !== undefined
      ? (open: boolean) => {
          // Call onOpenChangeProp if it has been provided
          if (onOpenChangeProp) {
            onOpenChangeProp(open);
          } else {
            // If onOpenChangeProp is not provided, call onMenuClose or onMenuOpen
            // to maintain backward compatibility with the deprecated props
            if (!open && onMenuClose) {
              onMenuClose();
            } else if (open && onMenuOpen) {
              onMenuOpen();
            }
          }
        }
      : null;

  const onOpenChange = setControlledOpen ?? setUncontrolledIsOpen;

  const toggleIsOpen = React.useCallback(() => {
    onOpenChange(!isOpen);
  }, [isOpen, onOpenChange]);

  const onInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUncontrolledInputValue(event.target.value);
      onInputChangeProp?.(event.target.value);
    },
    [onInputChangeProp]
  );

  /**
   * We have to debounce the open state based on the debounce of the filter
   * to avoid flashing as the user types.
   */
  const debounceOpen = React.useMemo(
    () =>
      debounce((inputValue) => {
        if (inputValue && prevInputValueRef.current !== inputValue && !isOpen) {
          onOpenChange(true);
        }

        prevInputValueRef.current = inputValue;
      }, filterDebounceDelay),
    [filterDebounceDelay, isOpen, onOpenChange]
  );

  React.useEffect(() => {
    debounceOpen(uncontrolledInputValue);
  }, [debounceOpen, uncontrolledInputValue]);

  const {refs, context, floatingStyles} = useFloatingBase({
    isOpen,
    minWidth: dropdownStyles?.width,
    maxWidth: dropdownStyles?.width,
    offsetModifier: () => ({verticalOffset: 2}),
    onOpenChange,
    placement: dropdownStyles?.placement || 'bottom-start',
    // If no dropdownWidth has been provided, the trigger and floating elements should match widths
    shouldMatchTriggerWidth: dropdownStyles?.width === undefined,
  });

  const handleInputBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      // Don't close the dropdown if focus is moving to an element within the floating UI
      // (e.g., clicking an option in the dropdown)
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      const isMovingToDropdown =
        relatedTarget && refs.floating.current?.contains(relatedTarget);

      if (!isMovingToDropdown) {
        onBlurProp?.(event);

        if (isOpen) {
          onOpenChange(false);
        }
      }
    },
    [isOpen, onBlurProp, onOpenChange, refs.floating]
  );

  const handleInputFocus = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (isFocusedFromCloseRef.current) {
        isFocusedFromCloseRef.current = false;
        return;
      }

      if (shouldOpenOnFocus && !isOpen) {
        onOpenChange(true);
      }

      onFocusProp?.(event);
    },
    [isOpen, onFocusProp, onOpenChange, shouldOpenOnFocus]
  );

  const handleOnChange = React.useCallback(
    (newValue: any, meta: any) => {
      let nextValue = newValue;

      // Reorderable multi-select: if the cursor is between chips when an item is
      // selected, insert the new item at the cursor in this single write (the
      // selection otherwise appends it). Doing it here — rather than appending
      // and reordering afterward — avoids a second write racing the field's
      // async persistence.
      if (
        isMultiValueReorderable &&
        selectionMode === 'multiple' &&
        Array.isArray(newValue) &&
        Array.isArray(value) &&
        newValue.length === value.length + 1
      ) {
        const caret = reorderCaretRef.current;
        const addedIndex = newValue.findIndex((v) => !value.includes(v));
        if (
          caret != null &&
          caret <= newValue.length - 1 &&
          addedIndex !== -1 &&
          addedIndex !== caret
        ) {
          nextValue = arrayMove(newValue, addedIndex, caret);
        }
      }

      if (onChange) {
        onChange(nextValue, meta);
      }

      // Clear the input value after selection
      setUncontrolledInputValue('');
      onInputChangeProp?.('');

      // Close the dropdown after selection
      if (shouldCloseOnSelect) {
        onOpenChange(false);
        isFocusedFromCloseRef.current = true;
      }
    },
    [
      onChange,
      onInputChangeProp,
      onOpenChange,
      shouldCloseOnSelect,
      isMultiValueReorderable,
      selectionMode,
      value,
    ]
  );

  const handleOnClear = React.useCallback(() => {
    const clearedValue = selectionMode === 'single' ? '' : [];
    handleOnChange(clearedValue, {option: null, options: []});
  }, [handleOnChange, selectionMode]);

  const handleOnCreate = React.useCallback(
    async (newValue: string) => {
      if (onCreateOption) {
        const result = await onCreateOption(newValue);

        /**
         * We explicitly check for result !== false instead of result === true
         * to account for the possibility that `onCreateOption` returns `undefined`.
         */
        if (result !== false) {
          // Creation succeeded — clear input and close dropdown
          setUncontrolledInputValue('');
          onInputChangeProp?.('');

          if (shouldCloseOnSelect) {
            onOpenChange(false);
            isFocusedFromCloseRef.current = true;
          }
        }
        // Creation failed — let the parent handle error display
      }
    },
    [onCreateOption, onInputChangeProp, onOpenChange, shouldCloseOnSelect]
  );

  const handleOnInputClick = React.useCallback(() => {
    if (shouldOpenOnFocus) {
      onOpenChange(true);
    }
  }, [onOpenChange, shouldOpenOnFocus]);

  /**
   * Internal render function passed to SearchableListWrapper.
   *
   * If consumer provided `renderOption`, use it directly for full control.
   * Otherwise, fall back to AutocompleteOption with `renderOptionLabel` support.
   */
  const renderOption = React.useCallback(
    (
      option: Parameters<
        NonNullable<AutocompleteProps<TOptions>['renderOptionLabel']>
      >[0],
      state: {isActive: boolean; categoryLabel?: string}
    ) => {
      if (renderOptionProp) {
        return renderOptionProp(option, state);
      }

      return (
        <AutocompleteOption
          option={option}
          renderOptionLabel={renderOptionLabelProp}
        />
      );
    },
    [renderOptionProp, renderOptionLabelProp]
  );

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const id = useFormGroupId(idProp);

  return (
    <SearchableListWrapper
      _isCreatable={isCreatable}
      _isOpen={isOpen}
      _onCreateOption={handleOnCreate}
      _renderCreateIcon={renderCreateIcon}
      _renderCreateLabel={renderCreateLabel}
      _shouldCloseOnScroll={shouldCloseOnScroll}
      checkmarkStyle={
        // Only support checkbox in multi-select mode
        selectionMode === 'multiple' && checkmarkStyle === 'checkbox'
          ? 'default'
          : // Default to icon
            'icon'
      }
      filterDebounceDelay={filterDebounceDelay}
      filterOptions={filterOptions}
      floatingContext={context}
      floatingRefs={refs}
      floatingStyles={floatingStyles}
      getSelectedOptionByValue={getSelectedOptionByValue}
      inputValue={uncontrolledInputValue}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onChange={handleOnChange}
      onInputChange={onInputChange}
      // @ts-expect-error - Need to figure out how to best handle these types. I believe it is due to the optional options prop, but not entirely sure yet. Once we resolve that the discriminated union type funkiness will probably return.
      options={options}
      // @ts-expect-error - Need to figure out how to best handle these types. I believe it is due to the optional options prop, but not entirely sure yet. Once we resolve that the discriminated union type funkiness will probably return.
      renderOption={renderOption}
      scrollContainerRef={scrollContainerRef}
      selectionMode={selectionMode}
      shouldFilterOverrideOptionsProp={shouldFilterOverrideOptionsProp}
      shouldHideSelectedOptions={shouldHideSelectedOptions}
      shouldNotBubbleDismiss
      value={value}
    >
      <AutocompleteInput
        {...props}
        aria-errormessage={ariaErrorMessage}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={className}
        data-automation-id={dataAutomationId}
        id={id}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isError={isError}
        isMultiValueReorderable={isMultiValueReorderable}
        multiValueColor={multiValueColor}
        multiValueVariant={multiValueVariant}
        onBlur={handleInputBlur}
        onClear={handleOnClear}
        onFocus={handleInputFocus}
        onInputClick={handleOnInputClick}
        onKeyDown={onKeyDown}
        onReorder={onReorder}
        onReorderCaretChange={handleReorderCaretChange}
        onReorderComplete={onReorderComplete}
        onTriggerClick={toggleIsOpen}
        placeholder={placeholder}
        ref={ref}
        // @ts-expect-error - Need to figure out how to best handle these types.
        renderOptionLabel={renderOptionLabelProp}
        // @ts-expect-error - Need to figure out how to best handle these types.
        renderSelectedLabel={renderSelectedLabelProp}
        selectionMode={selectionMode}
        shouldHideDropdownButton={shouldHideDropdownButton}
        shouldHideTagDismissButton={shouldHideTagDismissButton}
        tagMaxWidth={tagMaxWidth}
      />
      <AutocompleteListBox
        hasGroupDividers={hasGroupDividers}
        isCreatable={isCreatable}
        isVirtualized={isVirtualized}
        maxHeight={maxHeight}
        note={note}
        renderEmptyStateLabel={renderEmptyStateLabel}
        scrollContainerRef={scrollContainerRef}
        shouldPreventBodyScroll={shouldPreventBodyScroll}
      />
    </SearchableListWrapper>
  );
};

export const Autocomplete = fixedForwardRef(AutocompleteBase);

// @ts-expect-error - TS2339 - Property 'displayName' does not exist on type
Autocomplete.displayName = 'Autocomplete';
